import React, { memo, useState } from 'react';
import { EnhancedAlertBanner } from 'app/components/RsdForms/EnhancedAlertBanner';
import { useEventKeyDatePermissions } from 'app/utils/rbacHandler';
import { STATUS_VALUES } from 'utils/constants';
import { MonthlyDateBadge } from './MonthlyDateBadge';
import { MonthlyItemTitle } from './MonthlyItemTitle';
import { MonthlyMomentContextMenu } from './MonthlyMomentContextMenu';
import { MonthlyMomentDeleteDialog } from './MonthlyMomentDeleteDialog';
import { useMonthlyKeyDateActions } from '../hooks/useMonthlyKeyDateActions';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyKeyDateRowProps {
  item: MonthlyTimelineItem;
}

export const MonthlyKeyDateRow = memo(({ item }: MonthlyKeyDateRowProps) => {
  const { isAdmin } = useEventKeyDatePermissions();
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    notification,
    setNotification,
    selectedMoment,
    isEditModalOpen,
    showDeleteConfirmation,
    handleMenuAction,
    handleCloseEditModal,
    handleEditSuccess,
    handleConfirmDelete,
    handleCancelDelete,
    editInitialValues,
    itemType,
    deleteTitle,
    deleteMessage,
    deleteConfirmLabel,
    EditEventModal
  } = useMonthlyKeyDateActions();

  const showAdminMenu =
    isAdmin &&
    (item.projectType === 'Key Dates' || item.projectType === 'Key Date') &&
    item.status === STATUS_VALUES.ACTIVE &&
    item.issueId &&
    item.title &&
    (isHovered || isMenuOpen);

  return (
    <>
      {notification && (
        <EnhancedAlertBanner
          variant={notification.variant}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoHide={true}
          autoHideDelay={4000}
        />
      )}

      <div
        className={`${styles.monthlyInlineItem} ${styles.monthlyInlineItemKeyDate}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.monthlyInlineItemMain}>
          <MonthlyDateBadge item={item} laneLabel="Key Dates" />
          <div className={styles.monthlyItemTitleWithMenu}>
            <MonthlyItemTitle
              title={item.title || 'Untitled'}
              laneLabel="Key Dates"
              issueId={item.issueId || item.id}
            />
            {showAdminMenu && (
              <div
                className={styles.monthlyMomentMenuInline}
                onClick={event => event.stopPropagation()}
              >
                <MonthlyMomentContextMenu
                  onAction={action => handleMenuAction(action, item)}
                  onOpenChange={setIsMenuOpen}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditModalOpen && selectedMoment && editInitialValues && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          itemType={itemType}
          issueId={selectedMoment.issueId!}
          initialValues={editInitialValues}
        />
      )}

      {showDeleteConfirmation && selectedMoment && (
        <MonthlyMomentDeleteDialog
          title={deleteTitle}
          message={deleteMessage}
          confirmLabel={deleteConfirmLabel}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
});
